import NumberRating from 'components/Common/NumberRating'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import Carousel from 'LuxKit/Carousel/Carousel'
import type { ScrollPosition } from 'LuxKit/Carousel/utils'
import { getHorizontalScrollPosition, getElementContentWidth } from 'LuxKit/Carousel/utils'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import debounce from 'debounce-promise'
import type { OfferDetailReview } from './types'

const ReviewQuote = styled.div`
  border-left: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  display: flex;
  padding-left: ${rem(24)};
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(8)};
`

interface Props {
  rating?: number
  reviewsTotal?: number
  reviews: Array<OfferDetailReview>
}

function OfferDetailReviews({ rating, reviewsTotal, reviews }: Props) {
  const intl = useIntl()
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>('start')
  const carouselRef = useRef<HTMLDivElement | null>(null)

  const handleArrowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!carouselRef.current) {
      return
    }
    const direction = e.currentTarget.dataset.direction
    const contentWidth = getElementContentWidth(carouselRef.current)

    const nextLeft = direction === 'left'
      ? carouselRef.current.scrollLeft - contentWidth
      : carouselRef.current.scrollLeft + contentWidth

    carouselRef.current.scrollTo({
      left: nextLeft,
      behavior: 'smooth',
    })
  }, [])

  const handleScroll = useMemo(() => {
    return debounce(() => {
      if (carouselRef.current) {
        setScrollPosition(getHorizontalScrollPosition(carouselRef.current))
      }
    }, 32)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const showArrows = reviews.length > 1

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
        <Group direction="vertical" gap={4}>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'l47D_QDjb2zODJhHuDoF',
              defaultMessage: 'Reviews',
              description: 'Heading for hotel reviews section in hotel detail drawer',
            })}
          </Heading>
          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              total={reviewsTotal}
              variant="small"
              squareContainer
            />
          )}
        </Group>
        {showArrows && (
          <Group direction="horizontal" gap={0}>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={handleArrowClick}
              data-direction="left"
              disabled={scrollPosition === 'start'}
            >
              <LineAngleLeftBIcon />
            </IconButton>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={handleArrowClick}
              data-direction="right"
              disabled={scrollPosition === 'end'}
            >
              <LineAngleRightBIcon />
            </IconButton>
          </Group>
        )}
      </Group>

      <Carousel
        snap="start"
        gutterSize={0}
        width="100%"
        ref={carouselRef}
        onScroll={handleScroll}
      >
        {reviews.map((review) => (
          <ReviewQuote key={review.id}>
            <BodyText variant="medium" colour="light-foreground-alt">
              {review.content}
            </BodyText>
            <BodyText variant="medium" colour="light-foreground-alt" italics>
              {review.userName}
            </BodyText>
          </ReviewQuote>
        ))}
      </Carousel>
    </Group>
  )
}

export default OfferDetailReviews
